<script setup>
/**
 * 意图聚类工作台 —— 一共要出多少条片、还差哪些、下一条出谁。
 *
 * 行的来源是当前剧本的动作聚类快照，和 `cli.mjs seed` 使用同一份数据库分析及排序。
 * 已覆盖 / 已入队由服务端现算，前端不自己记。
 */
import { computed, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { Eye, Plus, RefreshCw, RotateCcw, Search, Sparkles } from '@lucide/vue'
import {
  Badge, Button, EmptyState, Input, Pagination, Section, Select,
  Slider, Spinner, Stat, Table, Toolbar, useToast,
} from '@drama/ui'

import {
  autoGenerateIntentVideoRequest, enqueueIntentVideo, listIntentClusters,
  rePromptIntentVideoRequest, retryIntentVideoRequest,
} from '../../api/intent-videos.js'
import { queueStatusLabel } from './format.js'

const props = defineProps({ scenarioId: { type: String, required: true } })

const { t } = useI18n()
const toast = useToast()
const router = useRouter()

const loading = ref(false)
const error = ref('')
const items = ref([])
const counts = ref(null)
const metrics = ref(null)
const staleness = ref(null)
// 三个维度各管各的，能叠加：「70% 目标里还没做的」= coverageTarget:70 + state:todo。
// coverageTarget=0 不限、minOccurrences=1 不限。两个都是滑块，不是写死的档位。
const filters = reactive({ production: 'all', coverage: 'all', coverageTarget: 0, minOccurrences: 1, q: '' })
const coverageTarget = ref(null)
const page = ref(1)
const pages = ref(1)
const pageSize = ref(50)
const matched = ref(0)
const enqueuingIntent = ref('')
const autoQueuingIntent = ref('')
const retryingIntent = ref('')
const rePromptingIntent = ref('')
const selectedIds = ref(new Set())
const batchRunning = ref(false)
const batchProgress = reactive({ done: 0, total: 0 })

const c = (key, params) => t(`visualGameAdmin.intentVideo.clusters.${key}`, params)

const productionOptions = computed(() => [
  { value: 'all', label: c('productionAll') },
  { value: 'none', label: c('production.none') },
  { value: 'active', label: c('production.active') },
  { value: 'done', label: c('production.done') },
  { value: 'failed', label: c('production.failed') },
])

const coverageOptions = computed(() => [
  { value: 'all', label: c('coverageAll') },
  { value: 'covered', label: c('coverage.covered') },
  { value: 'uncovered', label: c('coverage.uncovered') },
])

const columns = computed(() => [
  { key: 'selection', label: '', width: '44px', align: 'center' },
  { key: 'rank', label: '#', width: '64px', align: 'right', mono: true },
  { key: 'occurrences', label: t('visualGameAdmin.intentVideo.table.occurrences'), width: '90px', align: 'right', mono: true },
  { key: 'intent', label: t('visualGameAdmin.intentVideo.table.intent'), width: '360px' },
  { key: 'variants', label: t('visualGameAdmin.intentVideo.clusters.variants'), width: '100px', align: 'right', mono: true },
  { key: 'production', label: c('productionColumn'), width: '120px' },
  { key: 'coverage', label: c('coverageColumn'), width: '110px' },
  { key: 'actions', label: t('visualGameAdmin.common.actions'), width: '260px', align: 'right', fixed: 'right' },
])

// 只有还没入队/没在生产中的行允许批量勾选，跟单行按钮「加入自动队列」的可用条件（production === 'none'）一致。
const selectableRows = computed(() => items.value.filter(row => row.production === 'none'))
const selectedRows = computed(() => selectableRows.value.filter(row => selectedIds.value.has(row.cluster_id)))
const allSelected = computed(() => (
  selectableRows.value.length > 0 && selectedRows.value.length === selectableRows.value.length
))

function toggleRow(row, checked) {
  const next = new Set(selectedIds.value)
  if (checked) next.add(row.cluster_id)
  else next.delete(row.cluster_id)
  selectedIds.value = next
}

function toggleAll(checked) {
  selectedIds.value = checked
    ? new Set(selectableRows.value.map(row => row.cluster_id))
    : new Set()
}

onMounted(() => load())

async function load() {
  loading.value = true
  error.value = ''
  try {
    const data = await listIntentClusters({
      scenario_id: props.scenarioId,
      production: filters.production,
      coverage: filters.coverage,
      coverage_target: filters.coverageTarget,
      min_occurrences: filters.minOccurrences,
      q: filters.q.trim() || undefined,
      page: page.value,
      limit: pageSize.value,
    })
    items.value = data.items
    counts.value = data.counts
    metrics.value = data.metrics
    staleness.value = data.staleness ?? null
    // 页码以服务端夹回来的为准：换筛选之后页数会变，它比前端更清楚落在第几页。
    page.value = data.page
    pages.value = data.pages
    matched.value = data.matched
    coverageTarget.value = data.coverage_target
    // 换页/换筛选后这一页的可选行会变，勾选只保留当前页里还站得住的 cluster_id。
    const stillSelectable = new Set(data.items.filter(row => row.production === 'none').map(row => row.cluster_id))
    selectedIds.value = new Set([...selectedIds.value].filter(id => stillSelectable.has(id)))
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    loading.value = false
  }
}

/** 换筛选、换搜索、换每页条数都要回第一页，否则会停在一个不存在的页码上。 */
function applyFilters() {
  page.value = 1
  load()
}

function resetFilters() {
  filters.production = 'all'
  filters.coverage = 'all'
  filters.coverageTarget = 0
  filters.minOccurrences = 1
  filters.q = ''
  applyFilters()
}

function changePageSize(size) {
  pageSize.value = size
  applyFilters()
}

const summary = computed(() => t('visualGameAdmin.intentVideo.clusters.pageSummary', {
  n: matched.value, page: page.value, pages: pages.value,
}))

/** 定时任务连续失败 ≥2 次，或者从没成功过 / 距上次成功超过一天，都提示数据可能过期。 */
const staleWarning = computed(() => {
  if (!staleness.value?.stale) return ''
  return staleness.value.last_success_at
    ? c('staleWithLastSuccess', {
      n: staleness.value.consecutive_failures,
      at: new Date(staleness.value.last_success_at).toLocaleString(),
    })
    : c('staleNeverSucceeded', { n: staleness.value.consecutive_failures })
})

const ACTIVE = ['pending', 'prompting', 'prompt_ready', 'rendering']
const productionBucket = row => (ACTIVE.includes(row.production) ? 'active' : row.production)

const productionVariant = { none: 'neutral', active: 'info', done: 'success', failed: 'danger' }
const coverageVariant = { covered: 'success', uncovered: 'neutral' }

async function enqueue(row) {
  enqueuingIntent.value = row.intent
  try {
    const result = await enqueueIntentVideo({ scenario_id: props.scenarioId, intent_text: row.intent })
    toast.success(t(result.created
      ? 'visualGameAdmin.intentVideo.clusters.enqueued'
      : 'visualGameAdmin.intentVideo.clusters.merged'))
    await load()
  } catch (requestError) {
    toast.error(requestError.message, { duration: 0 })
  } finally {
    enqueuingIntent.value = ''
  }
}

/**
 * 加入队列，同时把「认领 → 调 lemonboss 写提示词 → 出片」这一整条链路自动跑起来——
 * 复用队列页「自动生成」按钮打的同一个接口，不重新做一遍判断。
 *
 * 这一行如果已经手动加入过队列（production === 'pending'，row.request_id 已经有值），
 * 就不用再 enqueue 一遍——直接对现有的 request_id 触发 auto-generate，
 * 不然「先手动入队、再点自动队列」会白白再打一次 enqueue 接口。
 *
 * 入队之后合并进了一条非 pending 的老请求（已经在写/已经出过片）时，
 * auto-generate 接口本身会报错，这里原样把这句话透出去，不额外分支。
 *
 * 单行按钮和批量按钮共用这一个函数：单行按 row 各自入队+触发生成，
 * toast/刷新列表这类只该发生一次的副作用留给各自的调用方处理。
 */
async function enqueueAutoRequest(row) {
  const requestId = row.request_id
    ?? (await enqueueIntentVideo({ scenario_id: props.scenarioId, intent_text: row.intent })).request_id
  await autoGenerateIntentVideoRequest(requestId)
}

async function enqueueAuto(row) {
  autoQueuingIntent.value = row.intent
  try {
    await enqueueAutoRequest(row)
    toast.success(t('visualGameAdmin.intentVideo.clusters.autoQueued'))
    await load()
  } catch (requestError) {
    toast.error(requestError.message, { duration: 0 })
  } finally {
    autoQueuingIntent.value = ''
  }
}

/**
 * 批量版：对每个勾选行依次跑一遍上面的入队+自动生成，串行执行——
 * 单条接口本来就没有批量入参，并发打只会互相抢生成资源。
 * 某一行失败不中断其余行，最后汇总成功/失败数量用 toast 告诉运营。
 */
async function batchEnqueueAuto() {
  const rows = selectedRows.value
  if (rows.length === 0) return

  batchRunning.value = true
  batchProgress.done = 0
  batchProgress.total = rows.length
  let succeeded = 0
  let failed = 0

  for (const row of rows) {
    try {
      await enqueueAutoRequest(row)
      succeeded += 1
    } catch (requestError) {
      failed += 1
    } finally {
      batchProgress.done += 1
    }
  }

  batchRunning.value = false
  selectedIds.value = new Set()
  if (failed === 0) {
    toast.success(t('visualGameAdmin.intentVideo.clusters.batchAutoQueuedAllOk', { n: succeeded }))
  } else {
    toast.error(t('visualGameAdmin.intentVideo.clusters.batchAutoQueuedPartial', { ok: succeeded, fail: failed }), { duration: 0 })
  }
  await load()
}

/**
 * 失败重来——跟队列页「重试」是同一个接口：已经有提示词的从出片那一步续，
 * 没有的回到队首重写。不走 enqueue（那是给全新意图去重登记用的），这一行
 * 已经有 request_id 了，直接对它重试。
 */
async function retry(row) {
  retryingIntent.value = row.intent
  try {
    await retryIntentVideoRequest(row.request_id)
    toast.success(t('visualGameAdmin.intentVideo.queue.retried'))
    await load()
  } catch (requestError) {
    toast.error(requestError.message, { duration: 0 })
  } finally {
    retryingIntent.value = ''
  }
}

/**
 * 已出片的重新来一遍——跟队列页「重写」是同一个接口：解绑现有资源、回队首，
 * 下一轮按当前规范重写。旧片子留在召回池顶着，新的出来之前覆盖率不掉。
 */
async function rePrompt(row) {
  rePromptingIntent.value = row.intent
  try {
    await rePromptIntentVideoRequest(row.request_id)
    toast.success(t('visualGameAdmin.intentVideo.queue.rePrompted'))
    await load()
  } catch (requestError) {
    toast.error(requestError.message, { duration: 0 })
  } finally {
    rePromptingIntent.value = ''
  }
}

/**
 * prompting/prompt_ready/rendering 这三个中间状态没有专属操作按钮——worker 正在自动
 * 推进，运营唯一能做的是去队列详情页看进度和已经写好的提示词，不是在这一页点什么。
 */
function openQueueDetail(row) {
  router.push({ name: 'intent-video-queue-detail', params: { requestId: row.request_id } })
}

function openDetail(row) {
  router.push({ name: 'intent-video-cluster-detail', params: { clusterId: String(row.cluster_id) }, query: { scenario_id: props.scenarioId } })
}
</script>

<template>
  <div class="ivc vga-page" data-insp-path="apps/visual-game-admin/src/pages/intent-video/ClustersTab.vue#clusters">
    <!-- 请求失败时整块不渲染。metrics 为空既可能是「没跑过聚类分析」也可能是「接口挂了」，
         两者显示同一句话，会让人对着一个 404 去跑脚本。 -->
    <Section v-if="error === ''">
      <div v-if="metrics" class="ivc__stats">
        <Stat :label="t('visualGameAdmin.intentVideo.clusters.totalTurns')" :value="String(metrics.total_turns)" />
        <Stat :label="t('visualGameAdmin.intentVideo.clusters.uniqueInputs')" :value="String(metrics.unique_inputs)" />
        <Stat :label="t('visualGameAdmin.intentVideo.clusters.totalClusters')" :value="String(metrics.total_clusters)" />
        <Stat
          :label="t('visualGameAdmin.intentVideo.clusters.needFor70')"
          :value="metrics.seventy_percent_mark ? String(metrics.seventy_percent_mark.clusters) : '-'"
        />
        <Stat v-if="counts" :label="c('production.none')" :value="String(counts.production.none)" />
        <Stat v-if="counts" :label="c('production.active')" :value="String(counts.production.active)" />
        <Stat v-if="counts" :label="c('production.done')" :value="String(counts.production.done)" />
        <Stat v-if="counts && counts.production.failed > 0" :label="c('production.failed')" :value="String(counts.production.failed)" />
        <Stat v-if="counts" :label="c('coverage.covered')" :value="String(counts.coverage.covered)" />
      </div>
      <p v-if="staleWarning" class="ivc__note ivc__note--warn">{{ staleWarning }}</p>
      <p v-if="counts && counts.queue_unmatched > 0" class="ivc__note ivc__note--warn">
        {{ c('queueUnmatched').replace('{n}', counts.queue_unmatched) }}
      </p>
      <p v-if="metrics" class="ivc__note">{{ t('visualGameAdmin.intentVideo.clusters.note') }}</p>
      <p v-else class="ivc__note">{{ t('visualGameAdmin.intentVideo.clusters.noClusterFile') }}</p>
    </Section>

    <Section v-if="error === ''">
      <Toolbar>
        <div class="drama-toolbar-field drama-toolbar-field--wide">
          <label>{{ t('visualGameAdmin.intentVideo.filters.search') }}</label>
          <Input
            v-model="filters.q"
            type="search"
            size="sm"
            :placeholder="t('visualGameAdmin.intentVideo.clusters.searchPlaceholder')"
            @keyup.enter="applyFilters"
          />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ c('productionColumn') }}</label>
          <Select v-model="filters.production" :options="productionOptions" size="sm" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ c('coverageColumn') }}</label>
          <Select v-model="filters.coverage" :options="coverageOptions" size="sm" />
        </div>
        <div class="drama-toolbar-field drama-toolbar-field--wide">
          <label>{{ t('visualGameAdmin.intentVideo.clusters.coverageTarget') }}</label>
          <Slider v-model="filters.coverageTarget" :min="0" :max="100" :step="5" @change="applyFilters">
            <template #value>
              {{ filters.coverageTarget === 0
                ? t('visualGameAdmin.intentVideo.clusters.coverageOff')
                : coverageTarget
                  ? t('visualGameAdmin.intentVideo.clusters.coverageNeeds', {
                    percent: filters.coverageTarget, rank: coverageTarget.rank,
                    rate: (coverageTarget.rate * 100).toFixed(1),
                  })
                  : `${filters.coverageTarget}%` }}
            </template>
          </Slider>
        </div>
        <div class="drama-toolbar-field drama-toolbar-field--wide">
          <label>{{ t('visualGameAdmin.intentVideo.clusters.minOccurrences') }}</label>
          <Slider
            v-model="filters.minOccurrences"
            :min="1"
            :max="metrics ? metrics.max_occurrences : 1"
            :step="1"
            @change="applyFilters"
          >
            <template #value>
              {{ filters.minOccurrences === 1
                ? t('visualGameAdmin.intentVideo.clusters.minOccurrencesOff')
                : t('visualGameAdmin.intentVideo.clusters.minOccurrencesValue', { n: filters.minOccurrences }) }}
            </template>
          </Slider>
        </div>
        <template #actions>
          <Button size="sm" :loading="loading" @click="applyFilters">
            <Search :size="14" /> {{ t('visualGameAdmin.intentVideo.filters.apply') }}
          </Button>
          <Button variant="ghost" size="sm" :disabled="loading" @click="resetFilters">
            <RotateCcw :size="14" /> {{ t('visualGameAdmin.common.reset') }}
          </Button>
        </template>
      </Toolbar>
    </Section>

    <div v-if="loading" class="vga-state"><Spinner /><span>{{ t('visualGameAdmin.common.loading') }}</span></div>
    <div v-else-if="error" class="vga-state">
      <p class="vga-error">{{ error }}</p>
      <Button variant="secondary" @click="load">{{ t('visualGameAdmin.common.retry') }}</Button>
    </div>
    <template v-else>
      <Section :padded="false">
        <template #actions>
          <label class="ivc__select-all">
            <input
              type="checkbox"
              :checked="allSelected"
              :disabled="selectableRows.length === 0"
              @change="toggleAll($event.target.checked)"
            />
            <span>{{ c('selectAll') }}</span>
          </label>
          <span v-if="batchRunning" class="ivc__batch-progress">
            {{ t('visualGameAdmin.intentVideo.clusters.batchProgress', { done: batchProgress.done, total: batchProgress.total }) }}
          </span>
          <Button
            size="sm"
            variant="secondary"
            :loading="batchRunning"
            :disabled="selectedRows.length === 0 || batchRunning"
            @click="batchEnqueueAuto"
          >
            <Sparkles :size="14" /> {{ t('visualGameAdmin.intentVideo.clusters.batchAutoQueue', { n: selectedRows.length }) }}
          </Button>
        </template>
        <EmptyState
          v-if="items.length === 0"
          :title="t('visualGameAdmin.intentVideo.clusters.emptyTitle')"
          :description="t('visualGameAdmin.intentVideo.clusters.emptyDescription')"
        />
        <Table v-else :columns="columns" :rows="items" row-key="cluster_id" clickable @row-click="openDetail">
          <template #selection="{ row }">
            <input
              class="ivc__row-checkbox"
              type="checkbox"
              :aria-label="t('visualGameAdmin.intentVideo.clusters.selectRow', { intent: row.intent })"
              :checked="selectedIds.has(row.cluster_id)"
              :disabled="row.production !== 'none'"
              @click.stop
              @change="toggleRow(row, $event.target.checked)"
            />
          </template>
          <template #intent="{ row }">
            <span class="ivc__ellipsis" :title="row.intent">{{ row.intent }}</span>
          </template>
          <template #variants="{ row }">
            {{ t('visualGameAdmin.intentVideo.clusters.variantCount', { n: row.variants }) }}
          </template>
          <!-- 排队中的直接显示队列里那一步（待写提示词/出片中/…），比笼统一个「排队中」有用 -->
          <template #production="{ row }">
            <Badge :variant="productionVariant[productionBucket(row)]" size="sm">
              {{ row.production === 'none'
                ? c('production.none')
                : queueStatusLabel(t, row.production) }}
            </Badge>
          </template>
          <template #coverage="{ row }">
            <Badge :variant="coverageVariant[row.coverage]" size="sm">{{ c(`coverage.${row.coverage}`) }}</Badge>
          </template>
          <template #actions="{ row }">
            <!-- pending = 已经手动加入过队列，但还没触发自动生成，仍然给「自动队列」一个入口；
                 「加入队列」再点一遍没有意义（已经在队列里了），只在 pending 之外隐藏它。 -->
            <div v-if="row.production === 'none' || row.production === 'pending'" class="ivc__actions">
              <Button
                variant="secondary"
                size="sm"
                :loading="autoQueuingIntent === row.intent"
                :disabled="enqueuingIntent === row.intent"
                @click.stop="enqueueAuto(row)"
              >
                <Sparkles :size="14" /> {{ t('visualGameAdmin.intentVideo.clusters.enqueueAuto') }}
              </Button>
              <Button
                v-if="row.production === 'none'"
                variant="ghost"
                size="sm"
                :loading="enqueuingIntent === row.intent"
                :disabled="autoQueuingIntent === row.intent"
                @click.stop="enqueue(row)"
              >
                <Plus :size="14" /> {{ t('visualGameAdmin.intentVideo.clusters.enqueue') }}
              </Button>
            </div>
            <div v-else-if="row.production === 'failed'" class="ivc__actions">
              <Button
                variant="ghost"
                size="sm"
                :loading="retryingIntent === row.intent"
                @click.stop="retry(row)"
              >
                <RotateCcw :size="14" /> {{ t('visualGameAdmin.common.retry') }}
              </Button>
            </div>
            <div v-else-if="row.production === 'done'" class="ivc__actions">
              <Button
                variant="ghost"
                size="sm"
                :loading="rePromptingIntent === row.intent"
                :title="t('visualGameAdmin.intentVideo.queue.rePromptHint')"
                @click.stop="rePrompt(row)"
              >
                <RefreshCw :size="14" /> {{ t('visualGameAdmin.intentVideo.queue.rePrompt') }}
              </Button>
            </div>
            <div v-else-if="row.request_id !== null" class="ivc__actions">
              <Button variant="secondary" size="sm" @click.stop="openQueueDetail(row)">
                <Eye :size="14" /> {{ t('visualGameAdmin.common.view') }}
              </Button>
            </div>
            <span v-else>-</span>
          </template>
        </Table>
      </Section>
      <Pagination
        :has-prev="page > 1"
        :has-next="page < pages"
        :loading="loading"
        :summary="summary"
        :prev-label="t('visualGameAdmin.intentVideo.pager.prev')"
        :next-label="t('visualGameAdmin.intentVideo.pager.next')"
        :page-size="pageSize"
        :page-size-options="[20, 50, 100]"
        :page-size-label="t('visualGameAdmin.intentVideo.pager.pageSize')"
        @prev="() => { page -= 1; load() }"
        @next="() => { page += 1; load() }"
        @update:page-size="changePageSize"
      />
    </template>
  </div>
</template>

<style scoped>
.ivc__stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); gap: var(--drama-space-md); }
.ivc__actions { display: flex; gap: var(--drama-space-xs); justify-content: flex-end; }
.ivc__note { margin: var(--drama-space-sm) 0 0; font-size: 12px; color: var(--drama-color-text-muted); }
.ivc__note--warn { color: var(--drama-color-warning, #8a5300); }
.ivc__ellipsis { display: block; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ivc__select-all { display: inline-flex; align-items: center; gap: var(--drama-space-xs); color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); cursor: pointer; }
.ivc__select-all input, .ivc__row-checkbox { width: 16px; height: 16px; accent-color: var(--drama-color-primary); cursor: pointer; }
.ivc__select-all input:disabled, .ivc__row-checkbox:disabled { cursor: not-allowed; opacity: 0.45; }
.ivc__batch-progress { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); font-variant-numeric: tabular-nums; }
</style>
